import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useThemes } from '../../../../../components/Theme/theme';
import { SkeletonLoader } from '../../../../../components/_components';
import { DeleteAccountModal } from '../../components/DeleteAccountModal';
import { ACCOUNT_DELETION_WARNINGS } from '../../data/accountDeletionData';
import { useDeleteAccount } from '../../hooks/useDeleteAccount';

export function DeleteAccountPage() {
  const { theme } = useThemes();
  const {
    description,
    setDescription,
    confirmationInput,
    setConfirmationInput,
    acknowledged,
    setAcknowledged,
    pendingRequest,
    isLoadingPending,
    isRequesting,
    isCancelling,
    showPendingState,
    canDelete,
    canSubmitModal,
    cancellableUntilLabel,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    handleRequestDeletion,
    handleCancelDeletion,
  } = useDeleteAccount();

  if (isLoadingPending) {
    return (
      <Box
        sx={{
          boxSizing: 'border-box',
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <SkeletonLoader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        maxWidth: 900,
        mx: 'auto',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderRadius: { xs: '8px', sm: '12px' },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 0.75 }}>
          Delete Account
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: { xs: 2, sm: 2.5 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Permanently remove your LedgerX account and personal access.
        </Typography>

        {showPendingState ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: { xs: '8px', sm: '10px' },
              bgcolor: (muiTheme) => alpha(muiTheme.palette.warning.main, 0.08),
              border: '1px solid',
              borderColor: 'warning.light',
              boxShadow: '0 4px 14px rgba(237, 108, 2, 0.12)',
            }}
          >
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              Deletion request is pending
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your account is scheduled for deletion.
              {cancellableUntilLabel ? (
                <>
                  {' '}
                  You can cancel this request until <strong>{cancellableUntilLabel}</strong>.
                </>
              ) : (
                ' You can cancel this request within the 7-day grace period.'
              )}
            </Typography>
            {pendingRequest?.description ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Reason provided: {pendingRequest.description}
              </Typography>
            ) : null}
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelDeletion}
              disabled={isCancelling}
              sx={{ mt: 1 }}
            >
              {isCancelling ? <CircularProgress size={20} /> : 'Cancel deletion request'}
            </Button>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: { xs: '8px', sm: '10px' },
              border: '1px solid',
              borderColor: 'error.light',
              bgcolor: 'background.paper',
              boxShadow: '0 6px 18px rgba(211, 47, 47, 0.1)',
            }}
          >
            <Typography fontWeight={600} sx={{ mb: 0.75, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
              Request account deletion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              Deleting your account will start a 7-day grace period. During this time you can cancel the
              request. After that, your account and access will be permanently removed.
            </Typography>

            {acknowledged ? (
              <Alert
                severity="error"
                icon={<WarningAmberRoundedIcon fontSize="inherit" />}
                sx={{ mb: { xs: 1.5, sm: 2 }, alignItems: 'flex-start', py: { xs: 0.5, sm: 1 } }}
              >
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Please review before continuing
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {ACCOUNT_DELETION_WARNINGS.map((warning) => (
                    <Typography component="li" variant="body2" key={warning} sx={{ mb: 0.75 }}>
                      {warning}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  color="error"
                  sx={{ py: 0.5 }}
                />
              }
              label="I want to delete my account and understand this action has consequences."
              sx={{
                mb: { xs: 1.5, sm: 2 },
                mx: 0,
                alignItems: 'flex-start',
                '& .MuiCheckbox-root': { pt: 0.25 },
                '& .MuiFormControlLabel-label': {
                  mt: 0.75,
                  lineHeight: 1.5,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 0.5, sm: 1 } }}>
              <Button
                variant="contained"
                color="error"
                onClick={openDeleteModal}
                disabled={!canDelete}
                fullWidth
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Delete account
              </Button>
            </Box>
          </Paper>
        )}
      </Paper>

      {!showPendingState ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: theme.palette.text.secondary }}>
          Need help instead? Contact support before deleting your account.
        </Typography>
      ) : null}
      {isDeleteModalOpen &&
        <DeleteAccountModal
          open={isDeleteModalOpen}
          description={description}
          confirmationInput={confirmationInput}
          isRequesting={isRequesting}
          canSubmit={canSubmitModal}
          onClose={closeDeleteModal}
          onDescriptionChange={setDescription}
          onConfirmationInputChange={setConfirmationInput}
          onConfirm={handleRequestDeletion}
        />
      }
    </Box>
  );
}

export default DeleteAccountPage;
